import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppLayout } from '@/components/AppLayout';
import { LoadingBlock } from '@/components/PageHeader';
import { RequireAuth } from '@/components/RequireAuth';
import { SafetyHomeRedirect } from '@/components/SafetyHomeRedirect';
import { NoticePopupWindow } from '@/pages/home/NoticePopupWindow';
import { LoginPage } from '@/pages/login/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PendingPage } from '@/pages/PendingPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';
import { SCREEN_REDIRECTS, SCREENS } from '@/screens';

/** 퇴사자만 여는 화면이라 앱 번들에 미리 싣지 않는다. */
const ExitSurveyPage = lazy(() => import('@/pages/exit/ExitSurveyPage'));

/** 식당 QR 이 여는 화면. 카메라로 찍어야 닿는 자리라 앱 번들에 미리 싣지 않는다. */
const MealCheckPage = lazy(() => import('@/pages/assets/MealCheckPage').then((m) => ({ default: m.MealCheckPage })));

/** 마이페이지의 「휴대폰 등록」 QR 이 여는 화면. 같은 이유로 미리 싣지 않는다. */
const MealPairPage = lazy(() => import('@/pages/assets/MealPairPage').then((m) => ({ default: m.MealPairPage })));

/**
 * AI Agent 모드 시안(사용자 지시 2026-10-26). <b>개발 빌드에만</b> 선다 — 운영 번들에서는 이 가지가
 * 통째로 빠져 화면 코드 조각도 만들어지지 않는다. `SCREENS` 밖이라 메뉴·권한·안내에 줄이 없다.
 */
const AgentModePage = import.meta.env.DEV
  ? lazy(() => import('@/pages/aiagent/AgentModePage').then((m) => ({ default: m.AgentModePage })))
  : null;

/**
 * 화면 라우팅.
 *
 * 라우트 목록은 `screens.tsx` 에서 만든다 — 이관 대상과 진행 상황이 한 파일에 모이고,
 * 라우터는 그 표를 레이아웃·가드로 감싸는 일만 한다.
 *
 * 주의: 라우트 가드는 화면 접근을 정리할 뿐 권한을 지키지 않는다.
 * 인가 판정은 백엔드 API 가 하며, 여기 가드는 UX 용이다.
 */
export const router = createBrowserRouter([
  // 로그인은 사이드바 바깥의 전체 화면이므로 AppLayout 을 쓰지 않는다.
  { path: '/login', element: <LoginPage /> },

  // 퇴사 설문(DOC-50 §5.3). **로그인하지 않은 사람**이 여는 유일한 화면이다 — 퇴사와 함께
  // 계정이 닫히기 때문이고, 인가는 주소의 토큰이 한다. 사이드바·가드 바깥에 세운다.
  {
    path: '/exit-survey/:token',
    element: (
      <Suspense fallback={<LoadingBlock />}>
        <ExitSurveyPage />
      </Suspense>
    ),
  },

  // 안전 관리는 안전 홈페이지(`safe/` 앱)로 떠났다(사용자 지시 2026-10-27). 옛 주소 — 할일 링크 ·
  // 인쇄된 이수확인서 QR(`/safety-sign/{token}`) — 는 가드 바깥에서 그대로 넘긴다(`lib/safetyHome.ts`).
  { path: '/safety', element: <SafetyHomeRedirect /> },
  { path: '/safety/*', element: <SafetyHomeRedirect /> },
  { path: '/safety-sign/*', element: <SafetyHomeRedirect /> },

  // 공지 팝업(사용자 지시 2026-09-10). 홈이 `window.open` 으로 여는 <b>따로 뜬 창</b>이라
  // 사이드바·상단바가 없다 — 껍데기가 함께 서면 그건 팝업이 아니라 작게 연 홈이다.
  //
  // 주소에 글 id 가 없다(사용자 지적 2026-09-10). 팝업에도 주소 줄은 반드시 보이므로, 거기
  // 36자리 UUID 가 뜨지 않게 id 는 창 이름으로 건넨다(`noticePopupOpener`).
  //
  // 인가는 API 가 한다: 볼 수 없는 글은 목록에서 빠지고, 그러면 창이 스스로 닫힌다.
  { path: '/notice-popup', element: <NoticePopupWindow /> },

  // 점심 체크(V294 · 사용자 지시 2026-09-17). 식당 벽의 QR 이 여는 자리라 <b>사이드바 바깥</b>에
  // 선다 — 배식 줄에 서서 몇 초 보는 화면에 껍데기가 함께 뜨면, 이 화면이 하는 말 한 줄이
  // 그 안 어딘가로 들어간다.
  //
  // 가드도 바깥이다. 로그인이 없을 때 <b>앱을 먼저 불러 보는</b> 일이 이 화면의 몫이고
  // (사용자 결정 2026-09-17 「앱 우선, 없으면 웹」), 가드 안에 두면 그 전에 로그인으로 튕긴다.
  // 인가는 API 가 한다 — 세션 없이 부르면 서버가 거절한다.
  {
    path: '/meals/check',
    element: (
      <Suspense fallback={<LoadingBlock />}>
        <MealCheckPage />
      </Suspense>
    ),
  },

  // 휴대폰 등록(V311). 마이페이지가 띄운 QR 을 폰으로 찍으면 여기로 온다 — 그 폰에는 세션이
  // 없다는 것이 이 길의 전제라, 위와 같은 이유로 가드 바깥에 선다.
  {
    path: '/meals/pair',
    element: (
      <Suspense fallback={<LoadingBlock />}>
        <MealPairPage />
      </Suspense>
    ),
  },

  {
    path: '/',
    element: <AppLayout />,
    // 껍데기(사이드바·헤더)가 터졌을 때. 화면 하나가 터진 것은 아래 각 라우트가 자기 자리에서 받는다 —
    // 여기까지 올려 보내면 사이드바가 함께 사라져 돌아갈 길이 없어진다.
    errorElement: <RouteErrorPage />,
    children: [
      ...SCREEN_REDIRECTS.map(({ from, to }) => ({
        // 라우터 트리의 루트는 index 라우트로 표현해야 한다.
        ...(from === '/' ? { index: true as const } : { path: from }),
        element: <Navigate to={to} replace />,
      })),

      ...SCREENS.map((screen) => ({
        path: screen.path,
        // 화면 코드를 받아오다 죽거나(배포 직후의 옛 조각) 그리다 터져도 <b>레이아웃 안에서</b> 받는다.
        errorElement: <RouteErrorPage />,
        element: (
          <RequireAuth menuKey={screen.menuKey}>
            {/* 화면 코드는 눌렀을 때 받아온다(`screens.tsx` 의 `lazy`). 받는 동안 스피너를 띄운다.
                가드 안쪽에 두어야 권한이 없는 화면의 코드를 받으러 가지 않는다. */}
            <Suspense fallback={<LoadingBlock />}>
              {screen.element ?? <PendingPage label={screen.label} v1Source={screen.v1Source} />}
            </Suspense>
          </RequireAuth>
        ),
      })),

      ...(AgentModePage
        ? [{
          path: '/aiagent/mode',
          errorElement: <RouteErrorPage />,
          element: (
            <RequireAuth menuKey="ai_agent">
              <Suspense fallback={<LoadingBlock />}>
                <AgentModePage />
              </Suspense>
            </RequireAuth>
          ),
        }]
        : []),

      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
